import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Field,
  Heading,
  Label,
  Paragraph,
  Select,
  Tag,
} from '@digdir/designsystemet-react';
import type {
  ActionPlan,
  MeasureStatus,
} from '../../../api/workEnvironmentTypes';
import { useSetMeasureStatusMutation } from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { longDate } from '../../Settings/privacyView';
import { dayAndMonth } from '../surveyProcessView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from './FollowUp.module.css';

const STATUSES: MeasureStatus[] = ['NOT_STARTED', 'IN_PROGRESS', 'DONE'];

const STATUS_COLORS: Record<MeasureStatus, 'neutral' | 'info' | 'success'> = {
  NOT_STARTED: 'neutral',
  IN_PROGRESS: 'info',
  DONE: 'success',
};

type Props = { surveyId: number; plan: ActionPlan; canFollowUp: boolean };

export const MeasuresCard = ({
  surveyId,
  plan,
  canFollowUp,
}: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const setStatus = useSetMeasureStatusMutation(surveyId);
  const statusLabel = (status: MeasureStatus) =>
    t(`workEnvironmentFollowUp.measures.statuses.${status}`);
  const editable = canFollowUp && Boolean(plan.approved);

  return (
    <section
      aria-labelledby="follow-up-measures-heading"
      className={process.card}
    >
      <div className={process.cardBody}>
        <div className={process.sideHeading}>
          <Heading level={2} data-size="sm" id="follow-up-measures-heading">
            {t('workEnvironmentFollowUp.measures.title')}
          </Heading>
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentFollowUp.measures.intro')}
          </Paragraph>
        </div>
        {setStatus.isError && (
          <Alert data-color="danger" data-size="sm">
            <Paragraph data-size="sm">
              {t('workEnvironmentFollowUp.measures.failed')}
            </Paragraph>
          </Alert>
        )}
        <ol className={classes.measures}>
          {plan.measures.map((measure, index) => (
            <li key={measure.id} className={classes.measure}>
              <div className={classes.measureText}>
                <div className={classes.measureTitle}>
                  <Heading level={3} data-size="2xs">
                    {index + 1}. {measure.title}
                  </Heading>
                  <Tag
                    data-color={STATUS_COLORS[measure.status]}
                    data-size="sm"
                  >
                    {statusLabel(measure.status)}
                  </Tag>
                </div>
                {measure.description && (
                  <Paragraph data-size="sm">{measure.description}</Paragraph>
                )}
                <Paragraph data-size="sm" className={classes.meta}>
                  <span>
                    <span className={process.subtle}>
                      {t('workEnvironmentFollowUp.measures.owner')}
                    </span>{' '}
                    {measure.owner ?? t('workEnvironmentPlan.builder.noOwner')}
                  </span>
                  <span>
                    <span className={process.subtle}>
                      {t('workEnvironmentFollowUp.measures.due')}
                    </span>{' '}
                    {dayAndMonth(measure.dueDate, i18n.language)}
                  </span>
                  {measure.completedAt && (
                    <span>
                      {t('workEnvironmentFollowUp.measures.completedOn', {
                        date: longDate(measure.completedAt, i18n.language),
                      })}
                    </span>
                  )}
                </Paragraph>
              </div>
              {editable && (
                <Field data-size="sm" className={classes.statusField}>
                  <Label htmlFor={`measure-${measure.id}-status`}>
                    {t('workEnvironmentFollowUp.measures.status')}
                  </Label>
                  <Select
                    id={`measure-${measure.id}-status`}
                    aria-label={t(
                      'workEnvironmentFollowUp.measures.statusFor',
                      { title: measure.title },
                    )}
                    value={measure.status}
                    onChange={(event) =>
                      setStatus.mutate({
                        measureId: measure.id,
                        status: event.target.value as MeasureStatus,
                      })
                    }
                  >
                    {STATUSES.map((status) => (
                      <Select.Option key={status} value={status}>
                        {statusLabel(status)}
                      </Select.Option>
                    ))}
                  </Select>
                </Field>
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};
